import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Bell } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL || '';
const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function ReferralNotificationsBell({ user }) {
  const { t, language } = useLanguage();
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const fetchEvents = async () => {
    if (!user) return;
    try {
      const r = await axios.get(`${API}/api/referrals/notifications`, { headers: getAuthHeader() });
      setEvents(r.data?.events || []);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    if (!user) { setEvents([]); return; }
    fetchEvents();
    const id = setInterval(fetchEvents, 60000);
    return () => clearInterval(id);
  }, [user]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const toggle = async () => {
    const willOpen = !open;
    setOpen(willOpen);
    if (willOpen && events.length > 0) {
      try {
        await axios.post(`${API}/api/referrals/notifications/mark-read`, {}, { headers: getAuthHeader() });
      } catch (e) { /* ignore */ }
      setTimeout(() => setEvents([]), 6000);
    }
  };

  if (!user || events.length === 0) return null;
  const count = events.length;

  return (
    <div ref={wrapRef} style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
      <button
        type="button"
        data-testid="ref-notif-bell"
        aria-label={t('virality.bellAria')}
        onClick={toggle}
        style={{
          position: 'relative',
          background: 'white',
          border: '1px solid #fbbf24',
          color: '#92400e',
          width: 30, height: 30, borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
        }}
        title={t('virality.bellTitle', { n: count })}
      >
        <Bell size={14} />
        <span
          style={{
            position: 'absolute', top: -4, right: -4,
            background: '#ef4444', color: 'white', fontSize: 10, fontWeight: 700,
            borderRadius: 10, minWidth: 16, height: 16,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '0 4px', border: '1.5px solid white',
          }}
        >{count}</span>
      </button>
      {open && (
        <div
          data-testid="ref-notif-panel"
          style={{
            position: 'absolute', top: 38, right: 0,
            minWidth: 260, maxWidth: 320,
            background: 'white', color: '#1f2937',
            borderRadius: 12, boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
            border: '1px solid #e5e7eb',
            zIndex: 10000,
            overflow: 'hidden',
          }}
        >
          <div style={{
            padding: '10px 14px', borderBottom: '1px solid #f3f4f6',
            fontSize: 13, fontWeight: 600, color: '#0a4797',
            background: 'linear-gradient(135deg, #f5f7ff 0%, #e8efff 100%)',
          }}>
            {t('virality.bellPanelTitle')}
          </div>
          <div style={{ maxHeight: 280, overflowY: 'auto' }}>
            {events.map((ev, i) => (
              <div
                key={i}
                data-testid={`ref-notif-item-${i}`}
                style={{
                  padding: '10px 14px',
                  borderBottom: '1px solid #f3f4f6',
                  display: 'flex', flexDirection: 'column', gap: 2,
                }}
              >
                <div style={{ fontSize: 14, fontWeight: 600 }}>
                  {ev.new_user_name || t('virality.bellAnonymous')}
                </div>
                <div style={{ fontSize: 12, color: '#64748b' }}>
                  {new Date(ev.ts).toLocaleString(language)}
                  {ev.points_awarded > 0 && (
                    <> · <b style={{ color: '#92400e' }}>+{ev.points_awarded}</b> {t('virality.abbrPoints')}</>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
